import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import { API } from "../../../../config";

const SimpleCarousel = ({ offers }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);

  // Create extended array with duplicate at the end for seamless loop
  const extendedOffers = [...offers, offers[0]];

  useEffect(() => {
    const timer = setInterval(() => {
      goToNextSlide();
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const goToSlide = (index) => {
    setIsTransitioning(true);
    setCurrentSlide(index);
  };

  const goToPrevSlide = () => {
    goToNextSlide(); // Both buttons go forward
  };

  const goToNextSlide = () => {
    if (currentSlide >= offers?.length - 1) {
      // If we're at the last real slide, go to duplicate
      setIsTransitioning(true);
      setCurrentSlide(offers?.length);
    } else {
      // Normal forward movement
      setIsTransitioning(true);
      setCurrentSlide((prev) => prev + 1);
    }
  };

  // Handle the seamless loop when we reach the duplicate slide
  useEffect(() => {
    if (currentSlide === offers?.length) {
      // We're at the duplicate slide, wait for transition to complete
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentSlide(0); // Jump to real first slide without animation

        // Re-enable transition after ensuring DOM has updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        });
      }, 500); // Match the transition duration

      return () => clearTimeout(timer);
    }
  }, [currentSlide, offers.length]);

  if (!offers?.length) return null;

  return (
    <div className="relative w-full md:h-[600px] overflow-hidden bg-white">
      {/* Slides */}
      <div
        className={`flex h-full ${
          isTransitioning ? "transition-transform duration-500 ease-in-out" : ""
        }`}
        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
      >
        {extendedOffers.map((offer, index) => (
          <Link
            to={`/product/${offer?.product?._id}`}
            key={`${offer._id}-${index}`}
            className="min-w-full h-full relative flex items-center justify-center"
          >
            <img
              src={`${API}/${offer.image}`}
              alt={offer.title}
              className="w-full h-full object-contain"
            />
          </Link>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-[#DA0E1A] text-white p-1 md:p-3 rounded-full hover:bg-[#990a12] transition-colors z-30"
        title="Previous slide"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      <button
        onClick={goToNextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-[#DA0E1A] text-white p-1 md:p-3 rounded-full hover:bg-[#990a12] transition-colors z-30"
        title="Next slide"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>

      {/* Bullet Points */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3 z-30">
        {offers &&
          offers?.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                currentSlide % offers.length === index
                  ? "bg-gray-800 w-8"
                  : "bg-gray-300 hover:bg-gray-400"
              }`}
              title={`Go to slide ${index + 1}`}
            />
          ))}
      </div>
    </div>
  );
};

export default SimpleCarousel;
